"use client";

import { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { toast } from "sonner";
import { useNavigate } from "react-router-dom";

interface DisputeActionsPanelProps {
  disputeId: string;
  dispute: {
    status: string;
    orderId: string;
    meta?: any;
    order: {
      areaId: string;
    };
  };
}

export function DisputeActionsPanel({ disputeId, dispute }: DisputeActionsPanelProps) {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);

  const [reviseDialogOpen, setReviseDialogOpen] = useState(false);
  const [discountType, setDiscountType] = useState<"PERCENT" | "FIXED">("PERCENT");
  const [discountValue, setDiscountValue] = useState<string>("");
  const [reviseNote, setReviseNote] = useState<string>("");

  const [returnDialogOpen, setReturnDialogOpen] = useState(false);
  const [returnSlots, setReturnSlots] = useState<any[]>([]);
  const [selectedReturnSlot, setSelectedReturnSlot] = useState<string>("");
  const [returnNote, setReturnNote] = useState<string>("");
  const [loadingSlots, setLoadingSlots] = useState(false);

  const [closeDialogOpen, setCloseDialogOpen] = useState(false);
  const [closeNote, setCloseNote] = useState<string>("");

  const canMarkUnderReview = dispute.status === "OPENED";
  const canTakeActions = ["OPENED", "UNDER_REVIEW"].includes(dispute.status);

  const handleMarkUnderReview = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/disputes/${disputeId}/under-review`, {
        method: "POST",
      });

      if (!response.ok) {
        throw new Error("Failed to mark dispute under review");
      }

      toast.success("Dispute marked under review");
      window.location.reload();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSendRevisedQuote = async () => {
    if (!discountValue || parseFloat(discountValue) < 0) {
      toast.error("Please enter a valid discount value");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/admin/disputes/${disputeId}/send-revised-quote`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          discountType,
          discountValue: parseFloat(discountValue),
          supportNote: reviseNote || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to send revised quote");
      }

      toast.success("Revised quote sent successfully");
      setReviseDialogOpen(false);
      window.location.reload();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const loadReturnSlots = async () => {
    if (returnSlots.length > 0) return;

    setLoadingSlots(true);
    try {
      const today = new Date().toISOString().split("T")[0];
      const response = await fetch(
        `/api/public/slots?type=RETURN&areaId=${dispute.order.areaId}&date=${today}`
      );

      if (response.ok) {
        const data = await response.json();
        setReturnSlots(data.slots || []);

        const preferredSlotId = dispute.meta?.preferredReturnSlotId;
        if (preferredSlotId && data.slots?.some((s: any) => s.id === preferredSlotId)) {
          setSelectedReturnSlot(preferredSlotId);
        }
      }
    } catch (error) {
      console.error("Error loading return slots:", error);
      toast.error("Failed to load return slots");
    } finally {
      setLoadingSlots(false);
    }
  };

  const handleApproveReturn = async () => {
    if (!selectedReturnSlot) {
      toast.error("Please select a return slot");
      return;
    }

    setLoading(true);
    try {
      const response = await fetch(`/api/admin/disputes/${disputeId}/approve-return`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          returnSlotId: selectedReturnSlot,
          supportNote: returnNote || undefined,
        }),
      });

      if (!response.ok) {
        const data = await response.json();
        throw new Error(data.error || "Failed to approve return");
      }

      toast.success("Return approved and scheduled");
      setReturnDialogOpen(false);
      window.location.reload();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  const handleCloseDispute = async () => {
    setLoading(true);
    try {
      const response = await fetch(`/api/admin/disputes/${disputeId}/close`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          supportNote: closeNote || undefined,
        }),
      });

      if (!response.ok) {
        throw new Error("Failed to close dispute");
      }

      toast.success("Dispute closed");
      setCloseDialogOpen(false);
      window.location.reload();
    } catch (error: any) {
      toast.error(error.message);
    } finally {
      setLoading(false);
    }
  };

  if (dispute.status === "CLOSED") {
    return (
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-muted-foreground">This dispute is closed.</p>
        </CardContent>
      </Card>
    );
  }

  return (
    <>
      <Card>
        <CardHeader>
          <CardTitle>Actions</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {canMarkUnderReview && (
            <Button
              onClick={handleMarkUnderReview}
              disabled={loading}
              variant="outline"
              className="w-full"
            >
              Mark Under Review
            </Button>
          )}

          {canTakeActions && (
            <>
              <Button
                onClick={() => setReviseDialogOpen(true)}
                disabled={loading}
                variant="default"
                className="w-full"
              >
                Send Revised Quote
              </Button>

              <Button
                onClick={() => {
                  setReturnDialogOpen(true);
                  loadReturnSlots();
                }}
                disabled={loading}
                variant="default"
                className="w-full"
              >
                Approve Return & Schedule
              </Button>
            </>
          )}

          <Button
            onClick={() => setCloseDialogOpen(true)}
            disabled={loading}
            variant="outline"
            className="w-full"
          >
            Close Dispute
          </Button>
        </CardContent>
      </Card>

      <Dialog open={reviseDialogOpen} onOpenChange={setReviseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Send Revised Quote with Discount</DialogTitle>
            <DialogDescription>
              Apply a discount to the current quote and send it to the customer.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="discountType">Discount Type</Label>
              <Select
                value={discountType}
                onValueChange={(value: "PERCENT" | "FIXED") => setDiscountType(value)}
              >
                <SelectTrigger id="discountType" className="mt-1.5">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="PERCENT">Percentage (%)</SelectItem>
                  <SelectItem value="FIXED">Fixed Amount</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="discountValue">
                Discount Value {discountType === "PERCENT" ? "(%)" : "(INR)"}
              </Label>
              <Input
                id="discountValue"
                type="number"
                min="0"
                step={discountType === "PERCENT" ? "0.1" : "1"}
                value={discountValue}
                onChange={(e) => setDiscountValue(e.target.value)}
                className="mt-1.5"
              />
            </div>
            <div>
              <Label htmlFor="reviseNote">Support Note (Optional)</Label>
              <Textarea
                id="reviseNote"
                value={reviseNote}
                onChange={(e) => setReviseNote(e.target.value)}
                className="mt-1.5"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviseDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleSendRevisedQuote} disabled={loading}>
              {loading ? "Sending..." : "Send Revised Quote"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={returnDialogOpen} onOpenChange={setReturnDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Approve Return & Schedule</DialogTitle>
            <DialogDescription>
              Select a return slot and approve the return request.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            {dispute.meta?.preferredReturnSlotId && (
              <div className="p-3 bg-muted rounded-lg text-sm">
                <p className="font-medium">Customer's preferred slot is pre-selected</p>
              </div>
            )}
            <div>
              <Label htmlFor="returnSlot">Return Slot</Label>
              {loadingSlots ? (
                <p className="text-sm text-muted-foreground mt-1.5">Loading slots...</p>
              ) : returnSlots.length > 0 ? (
                <Select value={selectedReturnSlot} onValueChange={setSelectedReturnSlot}>
                  <SelectTrigger id="returnSlot" className="mt-1.5">
                    <SelectValue placeholder="Select a slot" />
                  </SelectTrigger>
                  <SelectContent>
                    {returnSlots.map((slot: any) => (
                      <SelectItem key={slot.id} value={slot.id}>
                        {new Date(slot.startAt).toLocaleDateString()} -{" "}
                        {new Date(slot.startAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}{" "}
                        to{" "}
                        {new Date(slot.endAt).toLocaleTimeString([], {
                          hour: "2-digit",
                          minute: "2-digit",
                        })}
                        {" "}({slot.capacity - slot.bookedCount} left)
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <p className="text-sm text-destructive mt-1.5">No return slots available</p>
              )}
            </div>
            <div>
              <Label htmlFor="returnNote">Support Note (Optional)</Label>
              <Textarea
                id="returnNote"
                value={returnNote}
                onChange={(e) => setReturnNote(e.target.value)}
                className="mt-1.5"
                rows={3}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setReturnDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleApproveReturn}
              disabled={loading || !selectedReturnSlot}
            >
              {loading ? "Approving..." : "Approve & Schedule Return"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog open={closeDialogOpen} onOpenChange={setCloseDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Close Dispute</DialogTitle>
            <DialogDescription>
              Close this dispute with an optional note.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="closeNote">Support Note (Optional)</Label>
              <Textarea
                id="closeNote"
                value={closeNote}
                onChange={(e) => setCloseNote(e.target.value)}
                className="mt-1.5"
                rows={3}
                placeholder="Explain why this dispute is being closed..."
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCloseDialogOpen(false)}>
              Cancel
            </Button>
            <Button onClick={handleCloseDispute} disabled={loading}>
              {loading ? "Closing..." : "Close Dispute"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
}
